import React, { useState, useEffect } from 'react';
import {
  Calendar,
  MapPin,
  Clock,
  Send,
  FileText,
  Upload,
  CheckCircle,
  Video,
  X,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { submitEventCoverage, checkEventCoverage } from '../../utils/api/eventCoverageApi';
import api from '../../utils/api';

const content = {
  ne: {
    title: 'कार्यक्रम कभरेज',
    submitReport: 'रिपोर्ट बुझाउनुहोस्',
    coverageTitle: 'कभरेज शीर्षक',
    coverageContent: 'कभरेज विवरण',
    coverageContentPlaceholder: 'कार्यक्रमको बारेमा विस्तृत रिपोर्ट लेख्नुहोस्...',
    uploadPhotos: 'तस्वीरहरू अपलोड गर्नुहोस्',
    uploadVideo: 'भिडियो लिङ्क (वैकल्पिक)',
    submitCoverage: 'कभरेज बुझाउनुहोस्',
    coverageSubmitted: 'कभरेज सफलतापूर्वक बुझाइयो!',
    upcoming: 'आगामी',
    ongoing: 'चलिरहेको',
    past: 'सम्पन्न',
    deadline: 'समय सीमा',
    location: 'स्थान',
    date: 'मिति',
    noEvents: 'कुनै कार्यक्रम तोकिएको छैन',
    submitted: 'पेश गरिएको',
    backToEvents: 'पछाडि जानुहोस्',
    choosePhotos: 'तस्वीर चयन गर्नुहोस्',
    filesSelected: 'फाइलहरू चयन गरियो',
    alreadySubmitted: 'यो कार्यक्रमको कभरेज पहिले नै पेश गरिसकेको छ',
    failedSubmit: 'कभरेज पेश गर्न असफल भयो',
    loading: 'लोड हुँदैछ...',
  },
  en: {
    title: 'Event Coverage',
    submitReport: 'Submit Report',
    coverageTitle: 'Coverage Title',
    coverageContent: 'Coverage Details',
    coverageContentPlaceholder: 'Write a detailed report about the event...',
    uploadPhotos: 'Upload Photos',
    uploadVideo: 'Video Link (optional)',
    submitCoverage: 'Submit Coverage',
    coverageSubmitted: 'Coverage submitted successfully!',
    upcoming: 'Upcoming',
    ongoing: 'Ongoing',
    past: 'Completed',
    deadline: 'Deadline',
    location: 'Location',
    date: 'Date',
    noEvents: 'No events assigned',
    submitted: 'Submitted',
    backToEvents: 'Back to Events',
    choosePhotos: 'Choose photos',
    filesSelected: 'file(s) selected',
    alreadySubmitted: 'Coverage for this event has already been submitted',
    failedSubmit: 'Failed to submit coverage',
    loading: 'Loading...',
  },
};

const statusMap = {
  upcoming: 'upcoming',
  current: 'ongoing',
  ongoing: 'ongoing',
  past: 'past',
};

const EventCoverage = () => {
  const { language } = useLanguage();
  const t = content[language] || content.en;
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [coverageTitle, setCoverageTitle] = useState('');
  const [coverageContent, setCoverageContent] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [photos, setPhotos] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submittedEvents, setSubmittedEvents] = useState(new Set());

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const res = await api.get('/events');
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setEvents(data);

      // Check which events already have coverage
      const coverageChecks = await Promise.allSettled(
        data.map((ev) => checkEventCoverage(ev._id))
      );
      const submittedIds = new Set();
      coverageChecks.forEach((result, i) => {
        if (result.status === 'fulfilled' && result.value.exists) {
          submittedIds.add(data[i]._id);
        }
      });
      setSubmittedEvents(submittedIds);
    } catch (err) {
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    setSubmitting(true);
    setError('');
    try {
      // Upload photos first
      const photoUrls = [];
      for (const photo of photos) {
        const formData = new FormData();
        formData.append('image', photo);
        const uploadRes = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/upload/image`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`,
          },
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          photoUrls.push(uploadData.url);
        }
      }

      await submitEventCoverage({
        event: selectedEvent._id,
        title: coverageTitle,
        content: coverageContent,
        photos: photoUrls,
        videoLink: videoLink || undefined,
      });

      setSubmitted(true);
      setSubmittedEvents((prev) => new Set([...prev, selectedEvent._id]));
      setTimeout(() => {
        setSubmitted(false);
        setSelectedEvent(null);
        setCoverageTitle('');
        setCoverageContent('');
        setVideoLink('');
        setPhotos([]);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || t.failedSubmit);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ne-NP', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center rounded-3xl border border-primary-200 bg-primary-50 py-16">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary">
          <CheckCircle size={32} className="text-white" />
        </div>
        <h3 className="text-xl font-extrabold text-char-900">{t.coverageSubmitted}</h3>
      </div>
    );
  }

  if (selectedEvent) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelectedEvent(null)}
          className="flex items-center gap-2 text-sm font-semibold text-char-500 transition-colors hover:text-primary"
        >
          ← {t.backToEvents}
        </button>

        <h2 className="text-2xl font-extrabold text-char-900">{selectedEvent.title}</h2>

        <div className="flex flex-wrap gap-4 text-sm text-char-500">
          <div className="flex items-center gap-2">
            <Calendar size={14} />
            <span>{formatDate(selectedEvent.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={14} />
            <span>{selectedEvent.location}</span>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600 border border-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="rounded-3xl border border-char-100 bg-white p-5 shadow-card sm:p-6">
            <div className="space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.coverageTitle}</label>
              <input
                type="text"
                value={coverageTitle}
                onChange={(e) => setCoverageTitle(e.target.value)}
                required
                className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-3 text-sm font-semibold text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div className="mt-5 space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.coverageContent}</label>
              <textarea
                value={coverageContent}
                onChange={(e) => setCoverageContent(e.target.value)}
                required
                rows={8}
                placeholder={t.coverageContentPlaceholder}
                className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-3 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 resize-y min-h-[180px]"
              />
            </div>

            <div className="mt-5 space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.uploadPhotos}</label>
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-char-200 bg-char-50 px-4 py-6 transition-all hover:border-primary-300 hover:bg-primary-50">
                <Upload size={24} className="text-char-300" />
                <span className="mt-2 text-xs font-semibold text-char-500">
                  {t.choosePhotos}
                </span>
                <input type="file" multiple accept="image/*" onChange={(e) => setPhotos([...e.target.files])} className="hidden" />
              </label>
              {photos.length > 0 && (
                <div className="mt-2 space-y-2">
                  {Array.from(photos).map((photo, idx) => (
                    <div key={idx} className="flex items-center gap-2 rounded-xl bg-char-50 px-3 py-2">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt=""
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                      <span className="flex-1 text-xs text-char-600 truncate">{photo.name}</span>
                      <button
                        type="button"
                        onClick={() => setPhotos(Array.from(photos).filter((_, i) => i !== idx))}
                        className="text-char-400 hover:text-error"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5 space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.uploadVideo}</label>
              <input
                type="url"
                value={videoLink}
                onChange={(e) => setVideoLink(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-3 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-extrabold text-white shadow-green transition-all hover:-translate-y-0.5 hover:bg-primary-500 hover:shadow-green-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={16} />
            {submitting ? (language === 'ne' ? 'पेश गर्दै...' : 'Submitting...') : t.submitCoverage}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-extrabold text-char-900">{t.title}</h2>

      {loadingEvents ? (
        <div className="flex items-center justify-center rounded-3xl border border-char-100 bg-white py-16">
          <p className="text-sm text-char-400">{t.loading}</p>
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-char-100 bg-white py-16">
          <Calendar size={40} className="text-char-300" />
          <p className="mt-3 text-sm text-char-500">{t.noEvents}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {events.map((event) => {
            const eventStatus = statusMap[event.status] || 'upcoming';
            const isSubmitted = submittedEvents.has(event._id);
            return (
              <div
                key={event._id}
                className="rounded-3xl border border-char-100 bg-white p-5 shadow-card transition-all duration-200 hover:-translate-y-1 hover:shadow-card-hover sm:p-6"
              >
                <div className="flex items-start justify-between gap-2">
                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider ${
                      eventStatus === 'ongoing'
                        ? 'bg-primary-100 text-primary'
                        : eventStatus === 'past'
                        ? 'bg-char-100 text-char-500'
                        : 'bg-blue-100 text-blue-600'
                    }`}
                  >
                    {eventStatus === 'ongoing' ? t.ongoing : eventStatus === 'past' ? t.past : t.upcoming}
                  </span>
                  {isSubmitted && (
                    <span className="rounded-full bg-primary-100 px-2.5 py-1 text-[10px] font-extrabold text-primary">
                      {t.submitted}
                    </span>
                  )}
                </div>
                <h3 className="mt-3 text-base font-extrabold text-char-900">{event.title}</h3>
                <div className="mt-3 space-y-1.5 text-sm text-char-500">
                  <div className="flex items-center gap-2">
                    <Calendar size={13} />
                    <span>{formatDate(event.date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={13} />
                    <span className="line-clamp-1">{event.location}</span>
                  </div>
                </div>
                {!isSubmitted ? (
                  <button
                    onClick={() => setSelectedEvent(event)}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-100 py-2.5 text-sm font-extrabold text-primary transition-all hover:bg-primary hover:text-white"
                  >
                    <FileText size={15} />
                    {t.submitReport}
                  </button>
                ) : (
                  <div className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-char-100 py-2.5 text-sm font-extrabold text-char-500">
                    <CheckCircle size={15} />
                    {t.submitted}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventCoverage;
